import axios from "axios";
import React, { useContext, useCallback } from "react";
// import { BASE_URL } from "../constants/api_urls";
import { AppStateContext } from "../context/AppContext";
import apiPath from "../constants/apiPath";
import { Severty, ShowToast } from "../helper/toast";
import encryptDecrypt from "../helper/encryptDecrypt";

const client = axios.create({
  baseURL: apiPath.baseURL,
});

const useRequest = () => {
  const { setLoading } = useContext(AppStateContext);

  const request = useCallback(
    async ({
      url,
      method: tmethod,
      data,
      onSuccess,
      onError,
      header,
      onErrorSubmit,
      skipEncryption,
    }) => {
      const method = (tmethod || "GET").trim().toUpperCase();
      let token = localStorage.getItem("token")
        ? localStorage.getItem("token")
        : "";

      const headers = {
        ...header,
        Authorization: `Bearer ${token}`,
        "X-Skip-Encryption": skipEncryption ? "true" : "false",
      };

      setLoading(true);
      try {
        // 🔑 Get per-session encryption key from localStorage
        const sessionKey = localStorage.getItem("sessionEncryptionKey");

        // 🔒 Encrypt outgoing data
        let encryptedData = data;
        let headers = { 
          ...header, 
          Authorization: `Bearer ${token}`,
          "X-Skip-Encryption": skipEncryption ? "true" : "false",
        };

        if (data && method !== "GET" && !skipEncryption) {
          if (sessionKey) {
            // Use per-session AES key
            if (data instanceof FormData) {
              const formData = new FormData();
              const jsonPayload = {};

              // Separate files from text fields
              for (const [key, value] of data.entries()) {
                if (value instanceof File || value instanceof Blob) {
                  formData.append(key, value);
                } else {
                  jsonPayload[key] = value;
                }
              }

              // Encrypt text fields with session key
              if (Object.keys(jsonPayload).length > 0) {
                formData.append("payload", encryptDecrypt.encryptPayload(jsonPayload, sessionKey));
              }
              encryptedData = formData;
              delete headers["Content-Type"];
            } else {
              encryptedData = { payload: encryptDecrypt.encryptPayload(data, sessionKey) };
            }
          } else {
            // No session key yet — send data unencrypted
            // This happens for pre-auth requests (login, 2FA, key-exchange)
            encryptedData = data;
          }
        }

        const response = await client({
          url,
          method,
          data: encryptedData,
          headers,
        });

        // 🔓 Decrypt incoming response
        let responseData = response.data;
        if (responseData && responseData.payload && sessionKey) {
          responseData = encryptDecrypt.decryptPayload(responseData.payload, sessionKey);
        } else if (responseData && responseData.payload && !sessionKey) {
          // Pre-auth response — shouldn't be encrypted since no session key yet
          // But if it is, it means middleware used global key (shouldn't happen for pre-auth)
          responseData = responseData;
        }
        if (onSuccess) {
          setLoading(false);
          onSuccess(responseData);
        }
        if (onErrorSubmit) {
          onErrorSubmit(responseData);
        }
        setLoading(false);
        return responseData;
      } catch (err) {
        console.log("API Error:", err);
        // Extract backend message
        let errorMessage = "Something went wrong";

        const sessionKey = localStorage.getItem("sessionEncryptionKey");

        if (err?.response?.data?.payload && sessionKey) {
          const decryptedError = encryptDecrypt.decryptPayload(
            err.response.data.payload,
            sessionKey
          );

          errorMessage =
            decryptedError?.message ||
            decryptedError?.error ||
            errorMessage;

        } else {
          errorMessage =
            err?.response?.data?.message ||
            err?.response?.data?.error ||
            err?.message ||
            errorMessage;
        }

        if (Array.isArray(errorMessage)) {
          errorMessage = errorMessage[0];
        }

        // Handle unauthorized
        if (
          err?.response?.status === 401 ||
          errorMessage === "jwt expired"
        ) {
          localStorage.removeItem("token");
          localStorage.removeItem("user");
          localStorage.removeItem("refreshToken");
          localStorage.removeItem("session_id");
          localStorage.removeItem("sessionEncryptionKey");

          ShowToast("Logout Successfully", Severty.SUCCESS);

          window.location.assign("/login");
          return;
        }

        // ✅ Show backend error message
        ShowToast(errorMessage, Severty.ERROR);

        // Optional callback
        if (onError) {
          try {
            onError(errorMessage, err);
          } catch (cbErr) {
            console.error("Error in onError callback:", cbErr);
          }
        }

      } finally {
        setLoading(false);
      }
    },
    [setLoading]
  );

  return { request };
};

export default useRequest;
